import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { convert as romanize } from 'hangul-romanization';
import { drawVerticalDottedCellBorder } from './pdfTableStyles';

export const ACCOUNTING_DOCUMENT_CONFIG = Object.freeze({
  issuer: Object.freeze({
    name: 'SAS GNEGNEGNE',
    addressLines: Object.freeze(['4 Rue Dunois', '75013 Paris']),
    legalLines: Object.freeze(['SIREN 952 058 766 RCS PARIS', 'TVA FR58952058766'])
  }),
  vatRate: 0.2,
  // Export scopes: every active invoice of the version, or only what changed in it
  // (new numbers and cancellations) to send as a supplement to a delivered month.
  exportScopes: Object.freeze({
    all: Object.freeze({
      label: 'Toutes les factures de la version',
      listTitle: 'Liste des factures associées aux crédits',
      fileSuffix: ''
    }),
    additions: Object.freeze({
      label: 'Compléments seulement (ajouts et annulations de cette version)',
      listTitle: 'Factures complémentaires associées aux crédits',
      fileSuffix: '_Complement'
    })
  }),
  types: Object.freeze({
    tattoo: Object.freeze({ label: 'Tattoo', prefix: 'T', folder: 'Tattoo' }),
    deposit: Object.freeze({ label: 'Acompte', prefix: 'A', folder: 'Acomptes' }),
    commission: Object.freeze({ label: 'Commission', prefix: 'C', folder: 'Commissions' })
  })
});

const TYPE_ALIASES = Object.freeze({
  tattoo: 'tattoo',
  t: 'tattoo',
  acompte: 'deposit',
  deposit: 'deposit',
  a: 'deposit',
  d: 'deposit',
  commission: 'commission',
  c: 'commission'
});

const INVOICE_LIST_TABLE_LAYOUT = Object.freeze({
  marginLeft: 14,
  tableWidth: 220,
  titleFontSize: 12,
  cellPadding: 1.1,
  columnWidths: Object.freeze([38, 23, 55, 32, 24, 24, 24])
});

const toNumber = value => Number.parseFloat(value) || 0;

const toAscii = value => {
  const text = String(value || '-');
  return /[ㄱ-ㆎ가-힣]/.test(text) ? romanize(text) : text;
};

const sanitizeFilename = value => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[<>:"/\\|?*]/g, '_')
  .replace(/\s+/g, '_')
  .replace(/_+/g, '_')
  .replace(/^_|_$/g, '')
  .slice(0, 80);

const formatIsoDate = value => {
  if (!value) return '-';
  const dateOnly = String(value).slice(0, 10);
  const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value);
};

const normalizeType = (row, fallbackType) => {
  const source = row?.source_type || row?.payment_type || fallbackType || row?.type || '';
  return TYPE_ALIASES[String(source).trim().toLowerCase()] || 'tattoo';
};

const resolveSourceId = row => row?.source_id ?? row?.payment_id ?? row?.reservation_id ?? row?.commission_id ?? row?.id;

const getTotalsByPaymentMethod = rows => rows.reduce((totals, row) => {
  const method = toAscii(row.moyen || '-');
  totals.set(method, (totals.get(method) || 0) + row.ttc);
  return totals;
}, new Map());

const getSortedPaymentMethodTotals = rows => Array.from(getTotalsByPaymentMethod(rows).entries())
  .sort(([left], [right]) => left.localeCompare(right, 'fr'));

const formatPaymentMethodTotals = rows => {
  const entries = getSortedPaymentMethodTotals(rows);
  return entries.length > 0
    ? entries.map(([method, total]) => `${method}: ${total.toFixed(2)} EUR`).join('  |  ')
    : 'Aucun paiement';
};

export function getInvoiceReference(row) {
  const reference = typeof row?.invoice_reference === 'string' ? row.invoice_reference.trim() : '';
  if (!reference) {
    throw new Error('Invoice reference must be assigned by the accounting service');
  }
  return reference;
}

export function getOptionalInvoiceReference(row, fallback = '-') {
  const reference = typeof row?.invoice_reference === 'string' ? row.invoice_reference.trim() : '';
  return reference || fallback;
}

export function normalizeInvoiceRow(row, options = {}) {
  const sourceType = normalizeType(row, options.type);
  const config = ACCOUNTING_DOCUMENT_CONFIG.types[sourceType];
  const ttc = toNumber(row?.amount_effective ?? row?.ttc);
  const ht = row?.ht == null ? ttc / (1 + ACCOUNTING_DOCUMENT_CONFIG.vatRate) : toNumber(row.ht);
  const tva = row?.tva == null ? ttc - ht : toNumber(row.tva);
  const paymentDate = row?.payment_date_effective || row?.payment_date || null;
  const displayDate = paymentDate ? formatIsoDate(paymentDate) : (row?.date || '-');

  return {
    ...row,
    sourceType,
    sourceId: resolveSourceId(row),
    typeLabel: config.label,
    folder: config.folder,
    date: displayDate,
    paymentDate,
    rdvDate: row?.rdv_date ? formatIsoDate(row.rdv_date) : '-',
    client: row?.client_effective ?? row?.client ?? '-',
    moyen: row?.method_effective ?? row?.moyen ?? '-',
    ttc,
    ht,
    tva,
    reference: getInvoiceReference(row, { ...options, type: sourceType }),
    creditDate: row?.credit_date ? formatIsoDate(row.credit_date) : '-',
    creditAmount: toNumber(row?.credit_total),
    creditDescription: row?.credit_description || '-'
  };
}

function renderInvoicePage(doc, inputRow, pageIndex, totalPages, options = {}) {
  const row = normalizeInvoiceRow(inputRow, options);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = 20;

  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(0, 0, 0);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURE', pageWidth / 2, y, { align: 'center' });
  y += 15;
  doc.setLineWidth(0.5);
  doc.line(20, y, pageWidth - 20, y);
  y += 10;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Date: ${row.date}`, 20, y);
  y += 7;
  doc.text(`N° Facture: ${row.reference}`, 20, y);
  y += 7;
  doc.text(`Client: ${toAscii(row.client)}`, 20, y);
  y += 12;

  doc.setFont('helvetica', 'bold');
  doc.text('Emetteur:', 20, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.text(ACCOUNTING_DOCUMENT_CONFIG.issuer.name, 20, y);
  ACCOUNTING_DOCUMENT_CONFIG.issuer.addressLines.forEach(line => {
    y += 6;
    doc.text(line, 20, y);
  });
  y += 12;

  doc.line(20, y, pageWidth - 20, y);
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.text('Description', 20, y);
  doc.text('Qte', 100, y);
  doc.text('P.U. HT', 120, y);
  doc.text('Total HT', 160, y);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.text(toAscii(row.typeLabel), 20, y);
  doc.text('1', 100, y);
  doc.text(`${row.ht.toFixed(2)} EUR`, 120, y);
  doc.text(`${row.ht.toFixed(2)} EUR`, 160, y);
  y += 15;

  doc.line(20, y, pageWidth - 20, y);
  y += 10;
  doc.text(`Mode de paiement: ${toAscii(row.moyen)}`, 20, y);
  y += 15;
  doc.line(20, y, pageWidth - 20, y);
  y += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Total HT:', 120, y);
  doc.text(`${row.ht.toFixed(2)} EUR`, 160, y);
  y += 7;
  doc.text(`TVA (${Math.round(ACCOUNTING_DOCUMENT_CONFIG.vatRate * 100)}%):`, 120, y);
  doc.text(`${row.tva.toFixed(2)} EUR`, 160, y);
  y += 7;
  doc.setFontSize(12);
  doc.text('Total TTC:', 120, y);
  doc.text(`${row.ttc.toFixed(2)} EUR`, 160, y);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  ACCOUNTING_DOCUMENT_CONFIG.issuer.legalLines.forEach((line, index) => {
    doc.text(line, pageWidth / 2, pageHeight - 25 + index * 6, { align: 'center' });
  });

  if (totalPages > 1) {
    doc.setFontSize(8);
    doc.setTextColor(180, 180, 180);
    doc.text(`${pageIndex + 1} / ${totalPages}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
  }
}

export function createInvoicePdfBlob(rows, options = {}) {
  const selectedRows = Array.isArray(rows) ? rows : [rows];
  if (selectedRows.length === 0) return null;
  const doc = new jsPDF();
  selectedRows.forEach((row, index) => {
    if (index > 0) doc.addPage();
    renderInvoicePage(doc, row, index, selectedRows.length, options);
  });
  return doc.output('blob');
}

export function getInvoiceFilename(row, options = {}) {
  const normalized = normalizeInvoiceRow(row, options);
  const client = sanitizeFilename(toAscii(normalized.client)) || 'client';
  return `facture_${sanitizeFilename(normalized.reference)}_${client}.pdf`;
}

export function downloadInvoicePdf(rows, options = {}) {
  const selectedRows = Array.isArray(rows) ? rows : [rows];
  if (selectedRows.length === 0) return;
  const blob = createInvoicePdfBlob(selectedRows, options);
  const filename = selectedRows.length === 1
    ? getInvoiceFilename(selectedRows[0], options)
    : `factures_${options.year || ''}_${String(options.month || '').padStart(2, '0')}.pdf`;
  saveAs(blob, filename);
}

export function createInvoiceListPdfBlob(rows, year, month, options = {}) {
  const normalizedRows = rows.map(row => normalizeInvoiceRow(row, { year, month }));
  const cancelledRows = (options.cancelledRows || []).map(row => normalizeInvoiceRow(row, { year, month }));
  const totals = normalizedRows.reduce((acc, row) => {
    acc.all += row.ttc;
    acc[row.sourceType] += row.ttc;
    return acc;
  }, { all: 0, tattoo: 0, deposit: 0, commission: 0 });
  const doc = new jsPDF('l', 'mm', 'a4');
  const monthLabel = `${year}-${String(month).padStart(2, '0')}`;

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const scope = ACCOUNTING_DOCUMENT_CONFIG.exportScopes[options.scope] || ACCOUNTING_DOCUMENT_CONFIG.exportScopes.all;
  doc.text(scope.listTitle, 14, 18);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  const versionLabel = Number.isInteger(Number(options.version))
    ? ` — Version ${Number(options.version)}`
    : '';
  doc.text(`${monthLabel}${versionLabel} — ${ACCOUNTING_DOCUMENT_CONFIG.issuer.name}`, 14, 25);
  doc.setFont('helvetica', 'bold');
  doc.text(`Total général TTC: ${totals.all.toFixed(2)} EUR`, 283, 25, { align: 'right' });

  let y = 34;
  Object.entries(ACCOUNTING_DOCUMENT_CONFIG.types).forEach(([sourceType, typeConfig]) => {
    if (y > 168) {
      doc.addPage();
      y = 20;
    }

    const typeRows = normalizedRows.filter(row => row.sourceType === sourceType);
    const typeCancelledRows = cancelledRows.filter(row => row.sourceType === sourceType);
    doc.setTextColor(51);
    doc.setFontSize(INVOICE_LIST_TABLE_LAYOUT.titleFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text(
      typeConfig.label.toUpperCase(),
      INVOICE_LIST_TABLE_LAYOUT.marginLeft + (INVOICE_LIST_TABLE_LAYOUT.tableWidth / 2),
      y,
      { align: 'center' }
    );

    const activeTableRows = typeRows.map(row => [
        row.reference,
        row.date,
        toAscii(row.client),
        toAscii(row.moyen),
        row.ht.toFixed(2),
        row.tva.toFixed(2),
        row.ttc.toFixed(2)
      ]);
    const cancelledTableRows = typeCancelledRows.map(row => [
      row.reference,
      row.date,
      {
        content: 'ANNULÉE',
        styles: { fontStyle: 'bold', textColor: [130, 70, 70] }
      },
      '-',
      '-',
      '-',
      '-'
    ]);
    const tableBody = activeTableRows.length + cancelledTableRows.length > 0
      ? [...activeTableRows, ...cancelledTableRows]
      : [[{
        content: 'Aucune facture',
        colSpan: 7,
        styles: { halign: 'center', textColor: [140, 140, 140], fontStyle: 'italic' }
      }]];

    autoTable(doc, {
      startY: y + 4,
      head: [['Facture', 'Date', 'Client', 'Moyen', 'HT', 'TVA', 'TTC']],
      body: tableBody,
      foot: [[
        {
          content: `Totaux par moyen — ${formatPaymentMethodTotals(typeRows)}`,
          colSpan: 7,
          styles: { halign: 'left', fontStyle: 'normal' }
        }
      ], [
        {
          content: `${typeRows.length} facture(s)${typeCancelledRows.length > 0 ? ` · ${typeCancelledRows.length} annulée(s)` : ''}`,
          colSpan: 6,
          styles: { halign: 'right', fontStyle: 'bold' }
        },
        {
          content: `${totals[sourceType].toFixed(2)} EUR`,
          styles: { halign: 'right', fontStyle: 'bold' }
        }
      ]],
      styles: { fontSize: 7.2, cellPadding: INVOICE_LIST_TABLE_LAYOUT.cellPadding },
      headStyles: { fillColor: [248, 248, 245], textColor: [51, 51, 51] },
      footStyles: { fillColor: [250, 250, 248], textColor: [51, 51, 51] },
      margin: { left: INVOICE_LIST_TABLE_LAYOUT.marginLeft },
      tableWidth: INVOICE_LIST_TABLE_LAYOUT.tableWidth,
      columnStyles: {
        0: { cellWidth: INVOICE_LIST_TABLE_LAYOUT.columnWidths[0] },
        1: { cellWidth: INVOICE_LIST_TABLE_LAYOUT.columnWidths[1] },
        2: { cellWidth: INVOICE_LIST_TABLE_LAYOUT.columnWidths[2] },
        3: { cellWidth: INVOICE_LIST_TABLE_LAYOUT.columnWidths[3] },
        4: { cellWidth: INVOICE_LIST_TABLE_LAYOUT.columnWidths[4], halign: 'right' },
        5: { cellWidth: INVOICE_LIST_TABLE_LAYOUT.columnWidths[5], halign: 'right' },
        6: { cellWidth: INVOICE_LIST_TABLE_LAYOUT.columnWidths[6], halign: 'right' }
      },
      didDrawCell: data => drawVerticalDottedCellBorder(doc, data)
    });

    y = (doc.lastAutoTable?.finalY || y + 18) + 10;
  });

  const overallPaymentMethodTotals = getSortedPaymentMethodTotals(normalizedRows);
  const summaryHeight = 20 + Math.max(overallPaymentMethodTotals.length, 1) * 7;
  if (y + summaryHeight > 195) {
    doc.addPage();
    y = 20;
  }
  doc.setTextColor(51);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Synthèse générale', 183, y);
  autoTable(doc, {
    startY: y + 3,
    head: [['Moyen', 'Total TTC']],
    body: overallPaymentMethodTotals.length > 0
      ? overallPaymentMethodTotals.map(([method, total]) => [method, `${total.toFixed(2)} EUR`])
      : [['Aucun paiement', '0.00 EUR']],
    foot: [[
      { content: 'Total général TTC', styles: { halign: 'right', fontStyle: 'bold' } },
      { content: `${totals.all.toFixed(2)} EUR`, styles: { halign: 'right', fontStyle: 'bold' } }
    ]],
    margin: { left: 183, right: 14 },
    tableWidth: 100,
    styles: { fontSize: 7.5, cellPadding: 1.8 },
    headStyles: { fillColor: [248, 248, 245], textColor: [51, 51, 51] },
    footStyles: { fillColor: [250, 250, 248], textColor: [51, 51, 51] },
    columnStyles: {
      0: { cellWidth: 65 },
      1: { cellWidth: 35, halign: 'right' }
    },
    didDrawCell: data => drawVerticalDottedCellBorder(doc, data)
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(7);
    doc.setTextColor(160);
    doc.text(`${page} / ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 7, { align: 'center' });
  }
  return doc.output('blob');
}
